import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Coins, Plus, ArrowDownCircle, ArrowUpCircle, Gift } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const TYPE_STYLE = { purchase: "text-olive-bright", grant: "text-volt", spend: "text-blaze", refund: "text-muted-foreground" };

function GrantDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ query: "", user_id: "", amount: "5.00", reason: "" });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!f.query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/accounts?search=${encodeURIComponent(f.query.trim())}&limit=6`).then(({ data }) => setResults(data.accounts)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [f.query]);

  const save = async () => {
    if (!f.user_id) { toast.error("Pick an account first."); return; }
    setBusy(true);
    try {
      await api.post("/admin/platform/credits/grant", { user_id: f.user_id, amount_cents: Math.round(Number(f.amount) * 100), reason: f.reason || "admin grant" });
      toast.success("Credits granted.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="credit-grant-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Gift className="w-5 h-5 text-volt" /> Grant Credits</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Account</label>
            <input value={f.query} onChange={(e) => setF({ ...f, query: e.target.value, user_id: "" })} placeholder="Search username / email" data-testid="grant-search" className={inputCls} />
            {results.length > 0 && !f.user_id && (
              <div className="border border-sniper mt-1 divide-y divide-sniper max-h-40 overflow-y-auto">
                {results.map((u) => (
                  <button key={u.user_id} onClick={() => setF({ ...f, user_id: u.user_id, query: `@${u.username}` })} data-testid={`grant-pick-${u.username}`}
                    className="w-full text-left px-3 py-2 font-mono text-xs text-secondary-foreground hover:bg-white/5 hover:text-foreground">@{u.username} · {u.email}</button>
                ))}
              </div>
            )}
            {f.user_id && <p className="text-[10px] font-mono uppercase text-olive-bright mt-1">Selected: {f.query}</p>}
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Amount (USD)</label>
            <input type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="grant-amount" className={inputCls} />
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Reason</label>
            <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. goodwill / support" data-testid="grant-reason" className={inputCls} />
          </div>
          <button onClick={save} disabled={busy || !f.user_id} data-testid="grant-submit" className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">{busy ? "Granting…" : "Grant credits"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCredits() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const load = () => { setLoadErr(null); return api.get(`/admin/platform/credits`).then(({ data }) => setData(data)).catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load credits.")); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const s = data?.summary || {};
  return (
    <div className="text-foreground" data-testid="admin-credits">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Coins className="w-7 h-7 text-olive-bright" /> Credits</h2>
        <button onClick={() => setDialog(true)} data-testid="credit-grant-btn" className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-5 py-2.5 transition-colors"><Plus className="w-4 h-4" /> Grant Credits</button>
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2">Prepaid-credit ledger — purchases, admin grants, spend and refunds. A grant here credits the member's real spendable balance.</p>

      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <p className="text-[11px] font-mono uppercase text-muted-foreground" data-testid="credits-exclusion-note">
          {`Demo & test accounts excluded from every figure${(data?.excluded_count || 0) > 0 ? ` · ${data.excluded_count} hidden` : ""}.`}
        </p>
      </div>
      <Crosshair className="my-5" />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="credits-summary">
        {[["Purchased", s.purchased_cents, "text-olive-bright"], ["Granted", s.granted_cents, "text-volt"], ["Spent", s.spent_cents, "text-blaze"], ["Outstanding", s.outstanding_cents, "text-foreground"], ["Entries", null]].map(([l, v, c], i) => (
          <div key={l} className="border border-sniper bg-surface p-4"><div className="font-mono text-[10px] uppercase text-muted-foreground">{l}</div><div className={`font-heading text-2xl mt-1 ${c || ""}`}>{i === 4 ? (s.entries || 0) : money(v)}</div></div>
        ))}
      </div>

      {loadErr ? (
        <div className="border border-sniper bg-surface" data-testid="credits-table">
          <LoadFailed reason={loadErr} onRetry={load} testid="credits-load-failed" />
        </div>
      ) : (data?.entries || []).length === 0 ? (
        <div className="border border-sniper bg-surface p-10 text-center" data-testid="credits-empty">
          <Coins className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="font-heading uppercase tracking-tight text-lg text-foreground">No credit entries yet</p>
          <p className="font-mono text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">Purchases, admin grants, spend and refunds all show here. Grant credits to a member to create the first entry.</p>
          <button onClick={() => setDialog(true)} data-testid="credits-empty-grant" className="mt-4 inline-flex items-center gap-1.5 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-[11px] px-4 py-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Grant credits</button>
        </div>
      ) : (
      <div className="border border-sniper bg-surface overflow-x-auto" data-testid="credits-table">
        <table className="w-full font-mono text-xs">
          <thead><tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
            <th className="px-3 py-2.5">Type</th><th className="px-3 py-2.5">Account</th><th className="px-3 py-2.5">Amount</th><th className="px-3 py-2.5">Reason</th><th className="px-3 py-2.5">When</th>
          </tr></thead>
          <tbody className="divide-y divide-sniper">
            {(data?.entries || []).map((e) => (
              <tr key={e.entry_id} className="text-secondary-foreground" data-testid={`credit-row-${e.entry_id}`}>
                <td className={`px-3 py-2.5 uppercase flex items-center gap-1 ${TYPE_STYLE[e.type] || ""}`}>
                  {e.type === "spend" || e.type === "refund" ? <ArrowDownCircle className="w-3 h-3" /> : <ArrowUpCircle className="w-3 h-3" />}{e.type}
                </td>
                <td className="px-3 py-2.5 text-foreground">
                  {e.username ? `@${e.username}` : e.user_id}
                  {e.is_demo && <span className="ml-1 text-[9px] uppercase text-volt" data-testid={`credit-demo-${e.entry_id}`}>demo</span>}
                  {e.is_test && <span className="ml-1 text-[9px] uppercase text-muted-foreground" data-testid={`credit-test-${e.entry_id}`}>test</span>}
                </td>
                <td className={`px-3 py-2.5 ${e.type === "spend" || e.type === "refund" ? "text-blaze" : "text-olive-bright"}`}>{e.type === "spend" || e.type === "refund" ? "-" : "+"}{money(e.amount_cents)}</td>
                <td className="px-3 py-2.5">{e.reason}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {dialog && <GrantDialog onClose={() => setDialog(false)} onSaved={() => { setDialog(false); load(); }} />}
    </div>
  );
}
